import { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { categories, Category } from "../../mocks/categories";
import { supabase, SanPhamHanQuoc } from "../../lib/supabase";
import { useCompanies } from "../../hooks/useCompanyData";
import { createSlug, getProductUrl } from "../../lib/slug";
import SEO from "@/components/SEO";
import { BreadcrumbSchema, ItemListSchema } from "@/components/StructuredData";

const CategoriesPage = () => {
  const { categoryId } = useParams<{ categoryId?: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || searchParams.get("search") || "";
  const subParam = searchParams.get("sub") || "";
  const searchTypeParam = searchParams.get("type") || "all";

  const { companies, loading: companiesLoading } = useCompanies();

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSub, setSelectedSub] = useState<string>(subParam);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [expandedCats, setExpandedCats] = useState<Record<string, boolean>>({});

  // Mobile filter drawer
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Sticky category bar ref
  const stickyBarRef = useRef<HTMLDivElement>(null);

  // Real products from Supabase
  const [products, setProducts] = useState<SanPhamHanQuoc[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Real counts from Supabase
  const [parentCounts, setParentCounts] = useState<Record<string, number>>({});
  const [subCounts, setSubCounts] = useState<Record<string, number>>({});
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (categoryId) {
      const found = categories.find((c) => c.id === categoryId) || null;
      setSelectedCategory(found);
      if (found) {
        setExpandedCats((prev) => ({ ...prev, [found.id]: true }));
      }
    } else {
      setSelectedCategory(null);
    }
  }, [categoryId]);

  useEffect(() => {
    setSelectedSub(subParam);
  }, [subParam]);

  // Fetch real counts from Supabase
  useEffect(() => {
    const fetchCounts = async () => {
      const { data } = await supabase
        .from("san_pham_han_quoc")
        .select("category_id, sub_category_id");
      if (data) {
        const pCounts: Record<string, number> = {};
        const sCounts: Record<string, number> = {};
        (data as { category_id?: string; sub_category_id?: string }[]).forEach((row) => {
          if (row.category_id) pCounts[row.category_id] = (pCounts[row.category_id] || 0) + 1;
          if (row.sub_category_id) sCounts[row.sub_category_id] = (sCounts[row.sub_category_id] || 0) + 1;
        });
        setParentCounts(pCounts);
        setSubCounts(sCounts);
        setTotalCount(data.length);
      }
    };
    fetchCounts();
  }, []);

  // Fetch products from Supabase based on filters
  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        let query = supabase
          .from("san_pham_han_quoc")
          .select("id, name, name_kr, images, media_items, company_name, company_logo, category_id, category_name, sub_category_id, sub_category_name, description, price, is_featured, created_at")
          .order("created_at", { ascending: false });

        if (selectedCategory) {
          query = query.eq("category_id", selectedCategory.id);
        }
        if (selectedSub) {
          query = query.eq("sub_category_id", selectedSub);
        }
        if (localSearch.trim()) {
          query = query.or(`name.ilike.%${localSearch}%,description.ilike.%${localSearch}%,company_name.ilike.%${localSearch}%`);
        }

        const { data } = await query.limit(60);
        if (data) setProducts(data as SanPhamHanQuoc[]);
      } catch {
        // silent
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [selectedCategory, selectedSub, localSearch]);

  // Filter companies when searching
  const filteredCompanies = (() => {
    if (!localSearch.trim()) return [];
    if (searchTypeParam === "product") return [];
    const q = localSearch.toLowerCase();
    return companies.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.nameKr.toLowerCase().includes(q) ||
        c.descriptionVi.toLowerCase().includes(q) ||
        c.mainCategoryName.toLowerCase().includes(q)
    );
  })();

  const showCompanyResults = filteredCompanies.length > 0 && (searchTypeParam === "all" || searchTypeParam === "company");

  const toggleCat = (catId: string) => {
    setExpandedCats((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const handleCategorySelect = (cat: Category | null) => {
    setSelectedCategory(cat);
    setSelectedSub("");
    setFilterDrawerOpen(false);
  };

  const handleSubSelect = (sub: string, cat: Category) => {
    setSelectedCategory(cat);
    setSelectedSub(sub);
    setFilterDrawerOpen(false);
  };

  const siteUrl = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";

  const pageTitle = selectedCategory ? selectedCategory.name : "Tất Cả Sản Phẩm";
  const pageDescription = selectedCategory
    ? `${selectedCategory.description || `Khám phá sản phẩm ${selectedCategory.name} từ doanh nghiệp Hàn Quốc`}. Tìm kiếm, so sánh và liên hệ mua hàng qua nền tảng KCANIA.`
    : "Tất cả sản phẩm công nghệ Hàn Quốc: Carbon Nano, hóa chất, vật liệu mới, thiết bị sản xuất. Kết nối với 200+ doanh nghiệp Hàn Quốc qua KCANIA.";

  // Sidebar content (shared between desktop sidebar and mobile drawer)
  const SidebarContent = () => (
    <div className="space-y-2">
      {/* Search */}
      <div className="bg-[#F8F9FA] rounded-2xl p-4">
        <h3 className="font-semibold text-gray-900 text-sm mb-2">Tìm Kiếm</h3>
        <div className="relative">
          <input
            type="search"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder="Tên sản phẩm, công ty..."
            className="w-full pl-4 pr-10 py-2 text-sm bg-white border border-gray-200 rounded-xl outline-none text-gray-700 focus:border-[#004A99] transition-all"
            aria-label="Tìm kiếm sản phẩm"
          />
          <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" aria-hidden="true" />
        </div>
      </div>

      {/* Category tree */}
      <nav aria-label="Danh mục sản phẩm" className="bg-[#F8F9FA] rounded-2xl p-4">
        <h3 className="font-semibold text-gray-900 text-sm mb-3">Danh Mục</h3>

        {/* All */}
        <button
          onClick={() => handleCategorySelect(null)}
          className={`flex items-center justify-between w-full px-3 py-2 rounded-xl text-sm mb-1 cursor-pointer transition-colors text-left ${
            !selectedCategory ? "bg-[#004A99] text-white" : "text-gray-600 hover:bg-white hover:text-gray-900"
          }`}
        >
          <span className="font-medium">Tất Cả</span>
          <span className="text-xs opacity-70">{totalCount}</span>
        </button>

        {/* Category tree */}
        {categories.map((cat) => {
          const count = parentCounts[cat.id] ?? 0;
          const isSelected = selectedCategory?.id === cat.id;
          const isExpanded = expandedCats[cat.id] || isSelected;
          const hasSubs = cat.subCategories.length > 0;

          return (
            <div key={cat.id} className="mb-0.5">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleCategorySelect(cat)}
                  className={`flex-1 flex items-center justify-between px-3 py-2 rounded-xl text-sm cursor-pointer transition-colors text-left ${
                    isSelected && !selectedSub
                      ? "bg-[#004A99] text-white"
                      : isSelected
                      ? "bg-[#004A99]/10 text-[#004A99] font-semibold"
                      : "text-gray-700 hover:bg-white hover:text-gray-900"
                  }`}
                >
                  <span className="font-semibold line-clamp-1 text-xs">{cat.name}</span>
                  <span className="text-[10px] opacity-60 flex-shrink-0 ml-1">({count})</span>
                </button>
                {hasSubs && (
                  <button
                    onClick={() => toggleCat(cat.id)}
                    className={`w-6 h-6 flex items-center justify-center rounded-lg transition-colors cursor-pointer flex-shrink-0 ${
                      isSelected ? "text-[#004A99]" : "text-gray-400 hover:text-gray-700"
                    }`}
                    aria-label={isExpanded ? "Thu gọn" : "Mở rộng"}
                  >
                    {isExpanded ? <i className="ri-arrow-up-s-line text-xs" aria-hidden="true" /> : <i className="ri-arrow-down-s-line text-xs" aria-hidden="true" />}
                  </button>
                )}
              </div>

              {/* Subcategories */}
              {isExpanded && hasSubs && (
                <div className="ml-3 mt-0.5 border-l-2 border-gray-200 pl-2 space-y-0.5">
                  {cat.subCategories.map((sub) => {
                    const subCount = subCounts[sub.id] ?? 0;
                    const isSubSelected = selectedSub === sub.id && isSelected;
                    return (
                      <button
                        key={sub.id}
                        onClick={() => handleSubSelect(sub.id, cat)}
                        className={`flex items-center justify-between w-full px-2.5 py-1.5 rounded-lg text-xs cursor-pointer transition-colors text-left ${
                          isSubSelected
                            ? "bg-[#004A99]/15 text-[#004A99] font-semibold"
                            : "text-gray-500 hover:bg-white hover:text-gray-800"
                        }`}
                      >
                        <span className="line-clamp-1">{sub.name}</span>
                        <span className="text-[10px] opacity-60 flex-shrink-0 ml-1">({subCount})</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${pageTitle} – Sản Phẩm Công Nghệ Hàn Quốc | KCANIA`}
        description={pageDescription}
        keywords={`${pageTitle}, sản phẩm Hàn Quốc, công nghệ ${selectedCategory?.name || "Carbon Nano"}, mua hàng B2B, KCANIA${localSearch ? `, ${localSearch}` : ""}`}
      />
      <BreadcrumbSchema
        items={[
          { name: "Trang chủ", url: siteUrl || "/" },
          { name: "Danh Mục", url: `${siteUrl}/danh-muc` },
          ...(selectedCategory ? [{ name: selectedCategory.name, url: `${siteUrl}/danh-muc/${selectedCategory.id}` }] : []),
          ...(selectedCategory && selectedSub ? [{
            name: selectedCategory.subCategories.find((s) => s.id === selectedSub)?.name || selectedSub,
            url: `${siteUrl}/danh-muc/${selectedCategory.id}?sub=${selectedSub}`,
          }] : []),
        ]}
      />

      <Navbar />

      {/* Page Header */}
      <header className="bg-[#001428] pt-16 md:pt-24 pb-6 md:pb-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <nav aria-label="Đường dẫn điều hướng" className="flex items-center gap-2 text-gray-400 text-xs md:text-sm mb-3 md:mb-4 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">Trang Chủ</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-white whitespace-nowrap">{selectedCategory ? selectedCategory.name : "Tất Cả Danh Mục"}</span>
            {selectedSub && selectedCategory && (
              <>
                <i className="ri-arrow-right-s-line" aria-hidden="true" />
                <span className="text-white/70 whitespace-nowrap">
                  {selectedCategory.subCategories.find((s) => s.id === selectedSub)?.name}
                </span>
              </>
            )}
          </nav>
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white mb-1 md:mb-2 break-words">
                {selectedCategory ? selectedCategory.name : "Tất Cả Sản Phẩm"}
              </h1>
              {selectedCategory && (
                <p className="text-gray-400 text-xs md:text-sm max-w-2xl hidden md:block">{selectedCategory.description}</p>
              )}
              {searchQuery && (
                <p className="text-gray-400 mt-1 md:mt-2 text-xs md:text-sm">
                  Kết quả tìm kiếm: <span className="text-[#00AEEF] font-semibold">&quot;{searchQuery}&quot;</span>
                </p>
              )}
            </div>
            {selectedCategory && (
              <div className="hidden md:flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 flex-shrink-0">
                <span className="text-[#00AEEF] font-bold text-sm whitespace-nowrap">Code {selectedCategory.code}</span>
                <span className="text-gray-400 text-sm">•</span>
                <span className="text-gray-300 text-sm whitespace-nowrap">
                  {parentCounts[selectedCategory.id] ?? 0} sản phẩm
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── MOBILE: Sticky horizontal category bar ── */}
      <div
        ref={stickyBarRef}
        className="md:hidden sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm"
      >
        {/* Filter button + horizontal scroll categories */}
        <div className="flex items-center gap-2 px-4 py-2">
          <button
            onClick={() => setFilterDrawerOpen(true)}
            className="flex items-center gap-1.5 bg-[#004A99] text-white text-xs font-semibold px-3 py-2 rounded-lg flex-shrink-0 cursor-pointer whitespace-nowrap"
            aria-label="Mở bộ lọc sản phẩm"
          >
            <i className="ri-filter-3-line text-sm" aria-hidden="true" />
            Lọc sản phẩm
          </button>
          <div className="flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 pb-0.5">
              <button
                onClick={() => handleCategorySelect(null)}
                className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                  !selectedCategory ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                Tất cả ({totalCount})
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat)}
                  className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-full font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    selectedCategory?.id === cat.id ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {cat.name} ({parentCounts[cat.id] ?? 0})
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sub-category chips when a category is selected */}
        {selectedCategory && selectedCategory.subCategories.length > 0 && (
          <div className="overflow-x-auto scrollbar-hide px-4 pb-2">
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedSub("")}
                className={`flex-shrink-0 text-xs px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                  !selectedSub ? "bg-[#004A99]/15 text-[#004A99] font-semibold" : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                }`}
              >
                Tất cả
              </button>
              {selectedCategory.subCategories.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => setSelectedSub(selectedSub === sub.id ? "" : sub.id)}
                  className={`flex-shrink-0 text-xs px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                    selectedSub === sub.id ? "bg-[#004A99]/15 text-[#004A99] font-semibold" : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  {sub.name} ({subCounts[sub.id] ?? 0})
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── MOBILE: Filter Drawer ── */}
      {filterDrawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Bộ lọc sản phẩm">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFilterDrawerOpen(false)} />
          <div className="relative w-80 max-w-[85vw] bg-white h-full overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="font-bold text-gray-800 text-sm">Lọc sản phẩm</h2>
              <button onClick={() => setFilterDrawerOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer" aria-label="Đóng">
                <i className="ri-close-line text-gray-500" aria-hidden="true" />
              </button>
            </div>
            <div className="p-4">
              <SidebarContent />
            </div>
          </div>
        </div>
      )}

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-8">
        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-64 flex-shrink-0" aria-label="Bộ lọc danh mục">
            <SidebarContent />
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">

            {/* Company results when searching */}
            {showCompanyResults && (
              <section className="mb-6 md:mb-8" aria-label="Doanh nghiệp phù hợp">
                <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-building-2-line text-[#004A99]" aria-hidden="true" />
                  </div>
                  Doanh Nghiệp ({filteredCompanies.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {filteredCompanies.map((company) => (
                    <Link
                      key={company.id}
                      to={`/doanh-nghiep/${company.id}`}
                      className="group bg-white rounded-2xl border border-gray-100 p-3 md:p-4 hover:shadow-md transition-all cursor-pointer flex flex-col items-center text-center"
                    >
                      {company.logoUrl ? (
                        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden mb-2 md:mb-3 bg-white border border-gray-100 flex items-center justify-center p-1">
                          <img src={company.logoUrl} alt={`Logo ${company.name}`} className="w-full h-full object-contain" style={{ maxHeight: 56 }} />
                        </div>
                      ) : (
                        <div
                          className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex flex-col items-center justify-center mb-2 md:mb-3"
                          style={{ background: `linear-gradient(135deg, ${company.logoFrom}, ${company.logoTo})` }}
                        >
                          <i className={`${company.logoIcon} text-white text-lg md:text-xl`} aria-hidden="true" />
                          <span className="text-white font-black text-[10px]">{company.logoInitials}</span>
                        </div>
                      )}
                      <p className="font-semibold text-[#212529] text-xs md:text-sm group-hover:text-[#004A99] transition-colors line-clamp-2 mb-1 break-words">
                        {company.name}
                      </p>
                      <p className="text-xs text-gray-400 line-clamp-1 break-words">{company.mainCategoryName}</p>
                    </Link>
                  ))}
                </div>
                {products.length > 0 && (
                  <div className="mt-4 md:mt-6 border-t border-gray-100 pt-4 md:pt-6">
                    <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-box-3-line text-[#004A99]" aria-hidden="true" />
                      </div>
                      Sản Phẩm ({products.length})
                    </h2>
                  </div>
                )}
              </section>
            )}

            {/* Product count bar */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600 text-xs md:text-sm">
                {loadingProducts ? (
                  <span className="text-gray-400">Đang tải...</span>
                ) : (
                  <>
                    <strong className="text-gray-900">{products.length}</strong> sản phẩm
                    {localSearch && <span> cho &quot;<strong>{localSearch}</strong>&quot;</span>}
                  </>
                )}
              </p>
            </div>

            {/* Product Grid */}
            {loadingProducts ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5" aria-busy="true" aria-label="Đang tải sản phẩm">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-gray-100 rounded-2xl overflow-hidden animate-pulse">
                    <div className="h-36 md:h-44 bg-gray-200" />
                    <div className="p-3 md:p-4 space-y-2">
                      <div className="h-3 w-1/3 bg-gray-200 rounded" />
                      <div className="h-4 w-full bg-gray-200 rounded" />
                      <div className="h-3 w-2/3 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16 md:py-20">
                <i className="ri-search-line text-4xl md:text-5xl text-gray-300 block mb-4" aria-hidden="true" />
                <p className="text-gray-400 text-base md:text-lg font-medium">Không tìm thấy sản phẩm</p>
                <p className="text-gray-400 text-xs md:text-sm mt-2">Hãy thử từ khóa khác hoặc chọn danh mục khác</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5" role="list" aria-label="Danh sách sản phẩm">
                {products.map((product) => {
                  const rawMedia = (product as unknown as Record<string, unknown>).media_items;
                  const mediaItems: Array<{ url: string; type: string; is_cover?: boolean }> = Array.isArray(rawMedia)
                    ? rawMedia as Array<{ url: string; type: string; is_cover?: boolean }>
                    : [];
                  const cover = mediaItems.find((m) => m.is_cover && m.type === "image");
                  const firstImg = mediaItems.find((m) => m.type === "image");
                  const thumb = cover?.url ?? firstImg?.url ?? (Array.isArray(product.images) ? (product.images as string[])[0] ?? "" : "");
                  return (
                    <Link
                      key={product.id}
                      to={getProductUrl({ companyName: product.company_name ?? "", name: product.name })}
                      className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-all cursor-pointer block"
                      role="listitem"
                    >
                      <div className="relative h-40 md:h-44 overflow-hidden bg-gray-50 flex items-center justify-center">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={`${product.name} – ${product.company_name}`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[#004A99]/5">
                            <i className="ri-box-3-line text-[#004A99]/30 text-3xl md:text-4xl" aria-hidden="true" />
                          </div>
                        )}
                        {product.is_featured && (
                          <div className="absolute top-2 right-2 md:top-3 md:right-3">
                            <span className="bg-[#004A99] text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-medium whitespace-nowrap">Nổi Bật</span>
                          </div>
                        )}
                      </div>
                      <div className="p-3 md:p-4">
                        <div className="flex items-center gap-1 mb-1.5 md:mb-2 flex-wrap">
                          {product.sub_category_name && (
                            <span className="text-[10px] md:text-xs bg-[#EDF2FB] text-[#004A99] px-1.5 md:px-2 py-0.5 rounded-full whitespace-nowrap">{product.sub_category_name}</span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900 text-xs md:text-sm leading-snug mb-1 line-clamp-2 group-hover:text-[#004A99] transition-colors break-words">{product.name}</h3>
                        <p className="text-[10px] md:text-xs text-gray-400 mb-1.5 md:mb-2 truncate">{product.company_name}</p>
                        <p className="text-[10px] md:text-xs text-gray-500 line-clamp-2 mb-2 md:mb-3 leading-relaxed hidden md:block">{product.description}</p>
                        <div className="flex items-center justify-between pt-2 md:pt-3 border-t border-gray-100">
                          <span className="text-[10px] md:text-xs font-medium text-[#004A99] whitespace-nowrap">{product.price || "Liên hệ"}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Internal links to related categories */}
            {!selectedCategory && !localSearch && (
              <nav aria-label="Danh mục nhanh" className="mt-10 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-3 font-semibold uppercase tracking-wider">Danh mục nổi bật</p>
                <div className="flex flex-wrap gap-2">
                  {categories.slice(0, 8).map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/danh-muc/${cat.id}`}
                      className="text-xs bg-[#F8F9FA] border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors"
                    >
                      {cat.name}
                    </Link>
                  ))}
                  <Link to="/doanh-nghiep" className="text-xs bg-[#F8F9FA] border border-gray-200 hover:border-[#004A99]/30 text-gray-600 hover:text-[#004A99] px-3 py-1.5 rounded-lg transition-colors">
                    Doanh nghiệp Hàn Quốc
                  </Link>
                </div>
              </nav>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
